import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Heart, Shield, Users, Star, Target, Eye,
  ArrowRight, CheckCircle2
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

// Two elderly women walking together
const ABOUT_HERO = "https://images.unsplash.com/photo-1694857561361-b66b3d1a6cc8?w=800&auto=format&fit=crop&q=80";
// Couple walking down sidewalk
const ABOUT_SECONDARY = "https://images.unsplash.com/photo-1648414749870-282b53b1548f?w=600&auto=format&fit=crop&q=80";

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

const values = [
  { icon: Heart, title: 'Compassion', description: 'Every interaction is rooted in genuine care and empathy for those we support.' },
  { icon: Shield, title: 'Trust', description: 'We build lasting relationships through reliability, honesty, and respect.' },
  { icon: Star, title: 'Excellence', description: 'We strive to exceed expectations in every aspect of our service.' },
  { icon: Users, title: 'Connection', description: 'We believe meaningful relationships are essential to wellbeing.' },
];

const differentiators = [
  'Premium, non-regulated companionship focused on lifestyle support',
  'Carefully selected, compassionate companions',
  'Flexible arrangements tailored to individual needs',
  'Focus on dignity, independence, and emotional wellbeing',
  'Community-minded approach with local partnerships',
  'Transparent pricing with no hidden costs',
];

export const AboutPage = () => {
  return (
    <div className="min-h-screen bg-[#F7F9FC]">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 bg-white" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div {...fadeInUp}>
              <span className="text-sm font-semibold uppercase tracking-[0.2em] text-[#3A7BD5] mb-4 block">
                About AERW Lifestyle
              </span>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#1E4F8A] leading-tight mb-6">
                Companionship with Dignity, Warmth and Trust
              </h1>
              <p className="text-lg text-[#4A5568] leading-relaxed">
                AERW Lifestyle was founded with a simple belief: everyone deserves meaningful connection 
                and support that respects their independence and dignity. We provide premium non-regulated 
                companionship and lifestyle support across the United Kingdom.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="relative"
            >
              <div className="absolute -inset-4 bg-[#3A7BD5]/10 rounded-3xl transform rotate-3" />
              <img
                src={ABOUT_HERO}
                alt="About AERW Lifestyle"
                className="relative rounded-3xl shadow-2xl w-full h-[400px] lg:h-[500px] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 md:py-28 bg-[#F7F9FC]" data-testid="mission-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <motion.div {...fadeInUp}>
              <Card className="h-full bg-white border-[#E2E8F0]">
                <CardContent className="p-8 md:p-10">
                  <div className="w-14 h-14 bg-[#F7F9FC] rounded-2xl flex items-center justify-center mb-6">
                    <Target className="w-7 h-7 text-[#3A7BD5]" />
                  </div>
                  <h2 className="font-serif text-2xl md:text-3xl text-[#1E4F8A] mb-4">Our Mission</h2>
                  <p className="text-[#4A5568] leading-relaxed">
                    To provide premium, compassionate companionship and non-regulated lifestyle support 
                    that enhances wellbeing, preserves independence, and creates meaningful connections 
                    for individuals across the United Kingdom.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
            <motion.div {...fadeInUp} transition={{ delay: 0.1 }}>
              <Card className="h-full bg-white border-[#E2E8F0]">
                <CardContent className="p-8 md:p-10">
                  <div className="w-14 h-14 bg-[#F7F9FC] rounded-2xl flex items-center justify-center mb-6">
                    <Eye className="w-7 h-7 text-[#3A7BD5]" />
                  </div>
                  <h2 className="font-serif text-2xl md:text-3xl text-[#1E4F8A] mb-4">Our Vision</h2>
                  <p className="text-[#4A5568] leading-relaxed">
                    To be the most trusted name in lifestyle companionship, recognised for our commitment 
                    to dignity, warmth, and genuine human connection. We envision communities where no one 
                    feels isolated or unsupported.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 md:py-28 bg-white" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="text-sm font-semibold uppercase tracking-[0.2em] text-[#3A7BD5] mb-4 block">
              Our Values
            </span>
            <h2 className="font-serif text-3xl md:text-4xl text-[#1E4F8A] mb-4">
              The Principles That Guide Us
            </h2>
            <p className="text-[#4A5568] max-w-2xl mx-auto">
              Everything we do is rooted in these core values that define our approach to companionship.
            </p>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full bg-[#F7F9FC] border-[#E2E8F0] hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-sm">
                      <value.icon className="w-8 h-8 text-[#3A7BD5]" />
                    </div>
                    <h3 className="font-semibold text-xl text-[#1A1A1A] mb-3">{value.title}</h3>
                    <p className="text-sm text-[#4A5568] leading-relaxed">{value.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="py-20 md:py-28 bg-[#F7F9FC]" data-testid="difference-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div {...fadeInUp}>
              <span className="text-sm font-semibold uppercase tracking-[0.2em] text-[#3A7BD5] mb-4 block">
                What Sets Us Apart
              </span>
              <h2 className="font-serif text-3xl md:text-4xl text-[#1E4F8A] mb-6">
                Premium Companionship, Not Personal Care
              </h2>
              <p className="text-[#4A5568] leading-relaxed mb-8">
                AERW Lifestyle provides non-regulated companionship and lifestyle support. We focus on 
                social connection, friendly presence, and everyday assistance that helps individuals 
                maintain their independence and enjoy life to the fullest.
              </p>
              <ul className="space-y-4">
                {differentiators.map((item, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle2 className="w-5 h-5 text-[#3A7BD5] flex-shrink-0 mt-0.5" />
                    <span className="text-[#4A5568]">{item}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="absolute -inset-4 bg-[#1E4F8A]/5 rounded-3xl transform -rotate-3" />
              <img
                src={ABOUT_SECONDARY}
                alt="AERW Lifestyle companionship"
                className="relative rounded-3xl shadow-xl w-full h-[400px] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Important Notice */}
      <section className="py-16 bg-[#1E4F8A]/5" data-testid="notice-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center">
            <p className="text-[#4A5568] leading-relaxed">
              <strong className="text-[#1A1A1A]">Please note:</strong> AERW Lifestyle provides non-regulated 
              companionship and lifestyle support services only. We do not provide personal care, medical care, 
              or any CQC-regulated services. Our focus is on social companionship, emotional wellbeing, and 
              everyday lifestyle assistance.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-white" data-testid="about-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeInUp}>
            <h2 className="font-serif text-3xl md:text-4xl text-[#1E4F8A] mb-6">
              Ready to Learn More?
            </h2>
            <p className="text-lg text-[#4A5568] mb-8">
              Get in touch to discuss how our companionship services can support you or your loved ones.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
             <Link
  to="/contact#consultation-form"
  className="inline-flex items-center justify-center bg-[#1E4F8A] text-white px-8 py-4 rounded-full font-medium hover:bg-[#163A66] transition-colors"
>
  Contact Us Today
  <ArrowRight className="w-5 h-5 ml-2" />
</Link>

<a
  href="/services"
  className="inline-flex items-center justify-center border-2 border-[#1E4F8A] text-[#1E4F8A] px-8 py-4 rounded-full font-medium hover:bg-[#F7F9FC] transition-colors"
>
  View Our Services
</a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
